import {ReactElement, ReactNode, useState} from "react";
import styled from "styled-components";

import IconCross from "@/assets/iconCross.svg?react";
import IconValide from "@/assets/iconValide.svg?react";
import {Modal} from "@/components/Modal/Modal";
import {Modal as ModalType} from "@/ts/modal";

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  & > svg {
    width: 2rem;
    height: 2rem;
    &#iconValide {
      fill: ${({theme}) => theme.colors.validate};
    }
    &#iconRefuse {
      fill: ${({theme}) => theme.colors.alert};
    }
  }
`;

type useModalType = {
  ModalComponent: ReactElement;
  modalData: ModalType;
  setModalData: (modalData: ModalType) => void;
  updateContent: (content: ReactNode) => void;
  modalThemePrimary: () => void;
  modalThemeSecondary: () => void;
  modalThemeValidate: () => void;
  modalThemeAlert: () => void;
  openModal: () => void;
  closeModal: () => void;
  updateClose: (close: () => void) => void;
  modalErrorMessage: (message: string) => void;
  modalSuccessMessage: (message: string) => void;
  lockClose: () => void;
  unlockClose: () => void;
};

export function useModal(
  close: () => void | Promise<void>,
  modalContent: ReactNode | null
): useModalType {
  const [canClose, setCanClose] = useState(true);
  const [modalData, setModalData] = useState<ModalType>({
    isOpen: false,
    close: async () => {
      await close();
    },
    content: modalContent,
    theme: "secondary",
  });
  const openModal = (): void => {
    setModalData(prev => ({...prev, isOpen: true}));
  };
  const closeModal = (): void => {
    setModalData(prev => ({...prev, isOpen: false}));
  };
  const updateContent = (content: ReactNode): void => {
    setModalData(prev => ({...prev, content}));
  };

  const modalThemePrimary = (): void => {
    setModalData(prev => ({...prev, theme: "primary"}));
  };

  const modalThemeSecondary = (): void => {
    setModalData(prev => ({...prev, theme: "secondary"}));
  };

  const modalThemeValidate = (): void => {
    setModalData(prev => ({...prev, theme: "validate"}));
  };

  const modalThemeAlert = (): void => {
    setModalData(prev => ({...prev, theme: "alert"}));
  };
  const updateClose = (close: () => Promise<void> | void): void => {
    setModalData(prev => ({...prev, close}));
  };
  const lockClose = (): void => {
    setCanClose(false);
  };
  const unlockClose = (): void => {
    setCanClose(true);
  };
  const modalErrorMessage = (message: string): void => {
    modalThemeAlert();
    updateContent(
      <ErrorMessage>
        <IconCross id="iconRefuse" />
        <p>{message}</p>
      </ErrorMessage>
    );
  };
  const modalSuccessMessage = (message: string): void => {
    modalThemeValidate();
    updateContent(
      <ErrorMessage>
        <IconValide id="iconValide" />
        <p>{message}</p>
      </ErrorMessage>
    );
  };
  const ModalComponent = (
    <Modal
      isOpen={modalData.isOpen}
      close={async () => {
        if (canClose) {
          if (modalData.close) {
            await modalData.close();
          }
          closeModal();
        }
      }}
      theme={modalData.theme}>
      {modalData.content}
    </Modal>
  );
  return {
    ModalComponent,
    modalData,
    setModalData,
    updateContent,
    updateClose,
    modalThemePrimary,
    modalThemeSecondary,
    modalThemeValidate,
    modalThemeAlert,
    openModal,
    closeModal,
    modalErrorMessage,
    modalSuccessMessage,
    lockClose,
    unlockClose,
  };
}
